import { useState } from 'react';
import { Fab, Snackbar, Alert, CircularProgress, Tooltip, Box } from '@mui/material';
import { CameraAlt as CameraAltIcon } from '@mui/icons-material';
import { useUploadScreenshotMutation } from '@/store/apiSlice';
import { useI18nContext } from '@/i18n/i18n-react';

/**
 * Floating screenshot button shown to debug-enabled users. Captures the current
 * page with html2canvas and uploads it to the server for admins to review.
 */
const DebugScreenshotButton = () => {
    const { LL } = useI18nContext();
    const [uploadScreenshot] = useUploadScreenshotMutation();
    const [busy, setBusy] = useState(false);
    const [feedback, setFeedback] = useState<{
        severity: 'success' | 'error';
        message: string;
    } | null>(null);

    const handleCapture = async () => {
        if (busy) return;
        setBusy(true);
        try {
            // Let the button repaint with its spinner before html2canvas blocks
            // the main thread (capture can take a moment on large / mobile pages).
            await new Promise((resolve) =>
                requestAnimationFrame(() => requestAnimationFrame(resolve))
            );

            const { default: html2canvas } = await import('html2canvas');
            const canvas = await html2canvas(document.body, {
                logging: false,
                useCORS: true,
                backgroundColor: null,
                // Pin to 1x — on high-DPI phones the default devicePixelRatio (2-3x)
                // produces huge PNGs that can blow past the server upload limit.
                scale: 1,
                // Capture only what the user currently sees (the viewport), not the
                // whole scrollable page — crop to the visible rectangle.
                x: window.scrollX,
                y: window.scrollY,
                width: document.documentElement.clientWidth,
                height: document.documentElement.clientHeight,
                windowWidth: document.documentElement.clientWidth,
                windowHeight: document.documentElement.clientHeight,
                // Never capture the button itself.
                ignoreElements: (el) => (el as HTMLElement).dataset?.screenshotIgnore === 'true',
            });
            const image = canvas.toDataURL('image/png');

            await uploadScreenshot({
                image,
                url: window.location.href,
                width: window.innerWidth,
                height: window.innerHeight,
                userAgent: navigator.userAgent,
            }).unwrap();

            setFeedback({ severity: 'success', message: LL.debugScreenshot.success() });
        } catch {
            setFeedback({ severity: 'error', message: LL.debugScreenshot.error() });
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Tooltip title={LL.debugScreenshot.tooltip()} placement="left">
                <Fab
                    color="primary"
                    aria-label={LL.debugScreenshot.tooltip()}
                    onClick={handleCapture}
                    disabled={busy}
                    data-screenshot-ignore="true"
                    sx={{
                        position: 'fixed',
                        left: 16,
                        bottom: 'max(env(safe-area-inset-bottom), 16px)',
                        // Sit above open modals/their backdrop so the button stays
                        // usable while a dialog is open (and can capture it too).
                        zIndex: (theme) => theme.zIndex.modal + 100,
                    }}
                >
                    {busy ? (
                        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                            {/* Full faint track so the spinner reads as a complete ring */}
                            <CircularProgress
                                variant="determinate"
                                value={100}
                                size={30}
                                thickness={4}
                                sx={{ color: 'rgba(255,255,255,0.3)' }}
                            />
                            {/* Brighter spinning segment on top */}
                            <CircularProgress
                                variant="indeterminate"
                                disableShrink
                                size={30}
                                thickness={4}
                                sx={{
                                    color: 'common.white',
                                    position: 'absolute',
                                    left: 0,
                                }}
                            />
                        </Box>
                    ) : (
                        <CameraAltIcon />
                    )}
                </Fab>
            </Tooltip>

            <Snackbar
                open={!!feedback}
                autoHideDuration={4000}
                onClose={() => setFeedback(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                data-screenshot-ignore="true"
            >
                {feedback ? (
                    <Alert
                        severity={feedback.severity}
                        variant="filled"
                        onClose={() => setFeedback(null)}
                    >
                        {feedback.message}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </>
    );
};

export default DebugScreenshotButton;
